import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { vendorsAPI, agenciesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './VendorForm.css';

const DEFAULT_FEATURES = {
  chat: false,
  explore: true,
  vault: true,
  history: false,
  cryptoPayments: false,
  helioPayments: false,
  walletLogin: false,
  whatsappSupport: false,
  feedback: false,
  inviteCodeGate: false
};

const DEFAULT_CONFIG = {
  branding: {
    appName: '',
    tagline: '',
    logoUrl: '',
    logoIconUrl: '',
    faviconUrl: '',
    footerText: '',
    gaTrackingId: ''
  },
  theme: {
    defaultTheme: 'light',
    availableThemes: ['light', 'dark'],
    overrides: {}
  },
  features: { ...DEFAULT_FEATURES },
  links: {
    termsUrl: '/terms',
    privacyUrl: '/privacy',
    supportUrl: ''
  },
  auth: {
    privyAppId: '',
    loginMethods: ['email'],
    walletList: [],
    showWalletLoginFirst: false
  },
  agency: {
    forceAgency: true
  }
};

const THEME_OPTIONS = ['light', 'dark', 'solana', 'ethereum', 'bitcoin', 'base'];
const LOGIN_METHOD_OPTIONS = ['email', 'wallet', 'google', 'apple', 'sms'];
const WALLET_OPTIONS = ['metamask', 'coinbase_wallet', 'rainbow', 'wallet_connect_qr', 'detected_ethereum_wallets'];

// Color override fields grouped by category
const COLOR_OVERRIDE_FIELDS = [
  { group: 'Brand', fields: [
    { key: '--accent', label: 'Accent Color' },
    { key: '--accent-hover', label: 'Accent Hover' },
    { key: '--accent-light', label: 'Accent Light' },
    { key: '--accent-text', label: 'Accent Text' },
  ]},
  { group: 'Sidebar', fields: [
    { key: '--sidebar-bg', label: 'Background' },
    { key: '--sidebar-text', label: 'Text' },
    { key: '--sidebar-icon-active', label: 'Active Icon' },
    { key: '--sidebar-border', label: 'Border' },
  ]},
  { group: 'Page & Cards', fields: [
    { key: '--page-bg', label: 'Page Background' },
    { key: '--card-bg', label: 'Card Background' },
    { key: '--card-border', label: 'Card Border' },
    { key: '--border', label: 'Border' },
  ]},
  { group: 'Text', fields: [
    { key: '--text-primary', label: 'Primary Text' },
    { key: '--text-secondary', label: 'Secondary Text' },
    { key: '--text-on-accent', label: 'Text on Accent' },
  ]},
  { group: 'Topbar', fields: [
    { key: '--topbar-bg', label: 'Background' },
    { key: '--topbar-text', label: 'Text' },
    { key: '--topbar-border', label: 'Border' },
  ]},
  { group: 'Inputs', fields: [
    { key: '--input-bg', label: 'Input Background' },
    { key: '--input-border', label: 'Input Border' },
    { key: '--input-focus', label: 'Input Focus' },
  ]},
];

const VendorForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [agencies, setAgencies] = useState([]);

  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_code: '',
    vendor_name: '',
    contact_email: '',
    custom_domain: '',
    agency_id: '',
    is_active: true,
    config: JSON.parse(JSON.stringify(DEFAULT_CONFIG))
  });

  useEffect(() => {
    fetchAgencies();
    if (isEditMode) fetchVendor();
  }, [id]);

  const fetchAgencies = async () => {
    try {
      const response = await agenciesAPI.getAll({ limit: 100, status: 'approved' });
      if (response.data.success) {
        setAgencies(response.data.data.agencies);
      }
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
    }
  };

  const fetchVendor = async () => {
    try {
      setLoading(true);
      const response = await vendorsAPI.getById(id);
      if (response.data.success) {
        const vendor = response.data.data.vendor;
        const config = vendor.config || {};
        setFormData({
          vendor_id: vendor.vendor_id,
          vendor_code: vendor.vendor_code,
          vendor_name: vendor.vendor_name,
          contact_email: vendor.contact_email || '',
          custom_domain: vendor.custom_domain || '',
          agency_id: vendor.agency_id || '',
          is_active: Boolean(vendor.is_active),
          config: {
            branding: { ...DEFAULT_CONFIG.branding, ...config.branding },
            theme: { ...DEFAULT_CONFIG.theme, ...config.theme },
            features: { ...DEFAULT_CONFIG.features, ...config.features },
            links: { ...DEFAULT_CONFIG.links, ...config.links },
            auth: { ...DEFAULT_CONFIG.auth, ...config.auth },
            agency: { ...DEFAULT_CONFIG.agency, ...config.agency }
          }
        });
      }
    } catch (error) {
      console.error('Failed to fetch vendor:', error);
      setError('Failed to load vendor');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConfigChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [section]: {
          ...prev.config[section],
          [field]: value
        }
      }
    }));
  };

  const handleFeatureToggle = (feature) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        features: {
          ...prev.config.features,
          [feature]: !prev.config.features[feature]
        }
      }
    }));
  };

  const handleThemeToggle = (theme) => {
    setFormData(prev => {
      const current = prev.config.theme.availableThemes || [];
      const updated = current.includes(theme)
        ? current.filter(t => t !== theme)
        : [...current, theme];
      return {
        ...prev,
        config: {
          ...prev.config,
          theme: { ...prev.config.theme, availableThemes: updated }
        }
      };
    });
  };

  const handleLoginMethodToggle = (method) => {
    setFormData(prev => {
      const current = prev.config.auth.loginMethods || [];
      const updated = current.includes(method)
        ? current.filter(m => m !== method)
        : [...current, method];
      return {
        ...prev,
        config: {
          ...prev.config,
          auth: { ...prev.config.auth, loginMethods: updated }
        }
      };
    });
  };

  const handleColorOverride = (themeName, cssVar, value) => {
    setFormData(prev => {
      const overrides = { ...prev.config.theme.overrides };
      if (!overrides[themeName]) {
        overrides[themeName] = { variables: {} };
      } else {
        overrides[themeName] = { ...overrides[themeName], variables: { ...overrides[themeName].variables } };
      }

      if (value) {
        overrides[themeName].variables[cssVar] = value;
      } else {
        delete overrides[themeName].variables[cssVar];
        if (Object.keys(overrides[themeName].variables).length === 0) {
          delete overrides[themeName];
        }
      }

      return {
        ...prev,
        config: {
          ...prev.config,
          theme: { ...prev.config.theme, overrides }
        }
      };
    });
  };

  const getOverrideValue = (themeName, cssVar) => {
    return formData.config.theme.overrides?.[themeName]?.variables?.[cssVar] || '';
  };

  const handleWalletToggle = (wallet) => {
    setFormData(prev => {
      const current = prev.config.auth.walletList || [];
      const updated = current.includes(wallet)
        ? current.filter(w => w !== wallet)
        : [...current, wallet];
      return {
        ...prev,
        config: {
          ...prev.config,
          auth: { ...prev.config.auth, walletList: updated }
        }
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload = {
        vendor_name: formData.vendor_name,
        contact_email: formData.contact_email || null,
        custom_domain: formData.custom_domain || null,
        agency_id: formData.agency_id || null,
        is_active: formData.is_active,
        config: formData.config
      };

      if (!isEditMode) {
        payload.vendor_id = formData.vendor_id;
        payload.vendor_code = formData.vendor_code;
      }

      if (isEditMode) {
        await vendorsAPI.update(id, payload);
      } else {
        await vendorsAPI.create(payload);
      }

      navigate('/vendors');
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to save vendor';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">
          <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/vendors')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
              BACK TO VENDORS
            </button>
            <h1 className="page-title">{isEditMode ? 'EDIT VENDOR' : 'NEW VENDOR'}</h1>
          </div>
        </div>

        <div className="dashboard-content">
          <form className="vendor-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">{error}</div>}

            {/* Basic Information */}
            <div className="form-section">
              <h3 className="form-section-title">BASIC INFORMATION</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">VENDOR ID *</label>
                  <input
                    className="form-input"
                    name="vendor_id"
                    value={formData.vendor_id}
                    onChange={handleChange}
                    placeholder="e.g. acme-travel"
                    required
                    disabled={isEditMode}
                  />
                  {!isEditMode && <span className="form-hint">Lowercase alphanumeric + hyphens. Cannot be changed later.</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">VENDOR CODE *</label>
                  <input
                    className="form-input"
                    name="vendor_code"
                    value={formData.vendor_code}
                    onChange={handleChange}
                    placeholder="e.g. ACM"
                    required
                    disabled={isEditMode}
                    maxLength={10}
                  />
                  {!isEditMode && <span className="form-hint">2-10 uppercase alphanumeric. Cannot be changed later.</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">VENDOR NAME *</label>
                  <input
                    className="form-input"
                    name="vendor_name"
                    value={formData.vendor_name}
                    onChange={handleChange}
                    placeholder="e.g. Acme Travel"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CONTACT EMAIL</label>
                  <input
                    className="form-input"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="contact@acmetravel.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CUSTOM DOMAIN</label>
                  <input
                    className="form-input"
                    name="custom_domain"
                    value={formData.custom_domain}
                    onChange={handleChange}
                    placeholder="visas.acmetravel.com"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">LINKED AGENCY</label>
                  <select
                    className="form-select"
                    name="agency_id"
                    value={formData.agency_id}
                    onChange={handleChange}
                  >
                    <option value="">-- No Agency --</option>
                    {agencies.map(a => (
                      <option key={a.id} value={a.id}>{a.agency_name} ({a.agency_code})</option>
                    ))}
                  </select>
                </div>
                {isEditMode && (
                  <div className="form-group">
                    <label className="form-label">STATUS</label>
                    <div className="toggle-group">
                      <button
                        type="button"
                        className={`toggle-btn ${formData.is_active ? 'active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, is_active: true }))}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        className={`toggle-btn ${!formData.is_active ? 'active inactive' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, is_active: false }))}
                      >
                        Inactive
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Branding */}
            <div className="form-section">
              <h3 className="form-section-title">BRANDING</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">APP NAME</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.appName}
                    onChange={(e) => handleConfigChange('branding', 'appName', e.target.value)}
                    placeholder="Acme Visas"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">TAGLINE</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.tagline}
                    onChange={(e) => handleConfigChange('branding', 'tagline', e.target.value)}
                    placeholder="Your travel partner"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">LOGO URL</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.logoUrl}
                    onChange={(e) => handleConfigChange('branding', 'logoUrl', e.target.value)}
                    placeholder="/vendor-assets/acme-travel/logo.svg"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">LOGO ICON URL</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.logoIconUrl}
                    onChange={(e) => handleConfigChange('branding', 'logoIconUrl', e.target.value)}
                    placeholder="/vendor-assets/acme-travel/logo-icon.svg"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">FAVICON URL</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.faviconUrl}
                    onChange={(e) => handleConfigChange('branding', 'faviconUrl', e.target.value)}
                    placeholder="/vendor-assets/acme-travel/favicon.svg"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">FOOTER TEXT</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.footerText}
                    onChange={(e) => handleConfigChange('branding', 'footerText', e.target.value)}
                    placeholder="Powered by Acme Travel"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">GA TRACKING ID</label>
                  <input
                    className="form-input"
                    value={formData.config.branding.gaTrackingId}
                    onChange={(e) => handleConfigChange('branding', 'gaTrackingId', e.target.value)}
                    placeholder="G-XXXXXXXXXX (leave empty to disable)"
                  />
                </div>
              </div>
            </div>

            {/* Theme */}
            <div className="form-section">
              <h3 className="form-section-title">THEME</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">DEFAULT THEME</label>
                  <select
                    className="form-select"
                    value={formData.config.theme.defaultTheme}
                    onChange={(e) => handleConfigChange('theme', 'defaultTheme', e.target.value)}
                  >
                    {THEME_OPTIONS.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">AVAILABLE THEMES</label>
                  <div className="checkbox-group">
                    {THEME_OPTIONS.map(theme => (
                      <label key={theme} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(formData.config.theme.availableThemes || []).includes(theme)}
                          onChange={() => handleThemeToggle(theme)}
                        />
                        <span>{theme.charAt(0).toUpperCase() + theme.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Brand Colors */}
            {(formData.config.theme.availableThemes || []).length > 0 && (
              <div className="form-section">
                <h3 className="form-section-title">BRAND COLORS</h3>
                <span className="form-hint" style={{ marginBottom: 12, display: 'block' }}>
                  Override CSS variables per theme. Leave empty to use default colors.
                </span>
                {(formData.config.theme.availableThemes || []).map(themeName => (
                  <div key={themeName} className="color-theme-block">
                    <h4 className="color-theme-title">{themeName.charAt(0).toUpperCase() + themeName.slice(1)} Theme</h4>
                    {COLOR_OVERRIDE_FIELDS.map(group => (
                      <div key={group.group} className="color-group">
                        <span className="color-group-label">{group.group}</span>
                        <div className="color-row">
                          {group.fields.map(field => (
                            <div key={field.key} className="color-field">
                              <label className="color-label">{field.label}</label>
                              <div className="color-input-wrapper">
                                <input
                                  type="color"
                                  className="color-picker"
                                  value={getOverrideValue(themeName, field.key) || '#000000'}
                                  onChange={(e) => handleColorOverride(themeName, field.key, e.target.value)}
                                />
                                <input
                                  type="text"
                                  className="color-hex-input"
                                  value={getOverrideValue(themeName, field.key)}
                                  onChange={(e) => handleColorOverride(themeName, field.key, e.target.value)}
                                  placeholder={field.key}
                                />
                                {getOverrideValue(themeName, field.key) && (
                                  <button
                                    type="button"
                                    className="color-clear-btn"
                                    onClick={() => handleColorOverride(themeName, field.key, '')}
                                    title="Clear override"
                                  >
                                    &times;
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            {/* Features */}
            <div className="form-section">
              <h3 className="form-section-title">FEATURES</h3>
              <div className="feature-grid">
                {Object.keys(DEFAULT_FEATURES).map(feature => (
                  <label key={feature} className="feature-toggle">
                    <input
                      type="checkbox"
                      checked={formData.config.features[feature] || false}
                      onChange={() => handleFeatureToggle(feature)}
                    />
                    <span className="feature-slider"></span>
                    <span className="feature-name">
                      {feature.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Links */}
            <div className="form-section">
              <h3 className="form-section-title">LINKS</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">TERMS URL</label>
                  <input
                    className="form-input"
                    value={formData.config.links.termsUrl}
                    onChange={(e) => handleConfigChange('links', 'termsUrl', e.target.value)}
                    placeholder="https://acmetravel.com/terms"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">PRIVACY URL</label>
                  <input
                    className="form-input"
                    value={formData.config.links.privacyUrl}
                    onChange={(e) => handleConfigChange('links', 'privacyUrl', e.target.value)}
                    placeholder="https://acmetravel.com/privacy"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">SUPPORT URL</label>
                  <input
                    className="form-input"
                    value={formData.config.links.supportUrl}
                    onChange={(e) => handleConfigChange('links', 'supportUrl', e.target.value)}
                    placeholder="https://support.acmetravel.com"
                  />
                </div>
              </div>
            </div>

            {/* Auth */}
            <div className="form-section">
              <h3 className="form-section-title">AUTHENTICATION</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">PRIVY APP ID</label>
                  <input
                    className="form-input"
                    value={formData.config.auth.privyAppId}
                    onChange={(e) => handleConfigChange('auth', 'privyAppId', e.target.value)}
                    placeholder="Leave empty to use default Privy app"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">SHOW WALLET LOGIN FIRST</label>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle-btn ${formData.config.auth.showWalletLoginFirst ? 'active' : ''}`}
                      onClick={() => handleConfigChange('auth', 'showWalletLoginFirst', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formData.config.auth.showWalletLoginFirst ? 'active' : ''}`}
                      onClick={() => handleConfigChange('auth', 'showWalletLoginFirst', false)}
                    >
                      No
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">LOGIN METHODS</label>
                  <div className="checkbox-group">
                    {LOGIN_METHOD_OPTIONS.map(method => (
                      <label key={method} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(formData.config.auth.loginMethods || []).includes(method)}
                          onChange={() => handleLoginMethodToggle(method)}
                        />
                        <span>{method.charAt(0).toUpperCase() + method.slice(1)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">WALLET LIST</label>
                  <div className="checkbox-group">
                    {WALLET_OPTIONS.map(wallet => (
                      <label key={wallet} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={(formData.config.auth.walletList || []).includes(wallet)}
                          onChange={() => handleWalletToggle(wallet)}
                        />
                        <span>{wallet.replace(/_/g, ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Agency Config */}
            <div className="form-section">
              <h3 className="form-section-title">AGENCY CONFIGURATION</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">FORCE AGENCY ASSIGNMENT</label>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle-btn ${formData.config.agency.forceAgency ? 'active' : ''}`}
                      onClick={() => handleConfigChange('agency', 'forceAgency', true)}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${!formData.config.agency.forceAgency ? 'active' : ''}`}
                      onClick={() => handleConfigChange('agency', 'forceAgency', false)}
                    >
                      No
                    </button>
                  </div>
                  <span className="form-hint">If yes, applications from this vendor will always go to the linked agency.</span>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => navigate('/vendors')} disabled={submitting}>
                CANCEL
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'SAVING...' : (isEditMode ? 'UPDATE VENDOR' : 'CREATE VENDOR')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default VendorForm;
